/**
 * This file represents methods from shared/ we still use to operate directly on Firebase records.
 * Ideally, we'd be migrating away from keeping it like that, use our own API instead.
 */
import firebase from 'firebase'
import { db } from '@/db'
import { DriveDocument } from './types/drive'
import { IBaseDocument } from '@/api/types/base'

export type QuerySnapshotType = firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>

export type QueryDocumentSnapshotType =
  firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>

export type DbType = firebase.firestore.Firestore

/**
 * Helper, a bit primitive /shared services replacement
 * @param db A firebase initialized App
 * @param collectionName particular collection name
 * @param id document uuid
 * @param params future options
 * @returns a document
 */
export async function getDocumentByUuid(
  db: DbType,
  collectionName: string,
  id: string,
  params?: any,
): Promise<any> {
  const doc = await db.collection(collectionName).doc(id).get()
  const data = doc.data()
  if (!data) {
    return null
  }
  if (!params?.includeDeleted && data.deleted) {
    return null
  }
  return data
}

/**
 * Helper - obscure firebase db details, not much else
 *
 * @param db firebase
 * @param collectionName a collection to retrieve
 * @returns a snapshot
 */
async function getCollectionSnapshot(
  db: firebase.firestore.Firestore,
  collectionName: string,
): Promise<QuerySnapshotType> {
  return await db.collection(collectionName).get()
}

/**
 * Helper - turn QuerySnapShot into a readily-accesible array of elements
 *
 * @param snapshot a Firebase collection snapshot
 * @returns an array of a provided <T> type
 */
function mapSnapshotToDocuments<T extends IBaseDocument>(snapshot: QuerySnapshotType): Array<T> {
  return (snapshot.docs as QueryDocumentSnapshotType[]).map((doc) => doc.data() as T)
}

/**
 * Helper - retrieve a snapshot of a whole collection and cast it onto a type
 *
 * @param db firestore
 * @param collectionName a collection to be retrieved
 * @returns an array of elements of given type
 */
export async function getAllCollectionDocuments<T extends IBaseDocument>(
  db: firebase.firestore.Firestore,
  collectionName: string,
): Promise<T[]> {
  const snapshot = await getCollectionSnapshot(db, collectionName)
  return mapSnapshotToDocuments<T>(snapshot)
}

export const apiGetDrive = (uuid: string): Promise<DriveDocument | null> => {
  return getDocumentByUuid(db, 'drives', uuid)
}
